import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "../../utils";

const alertVariants = cva(
  cn(
    "al-relative al-w-full al-rounded-lg al-border al-p-4",
    "[&>svg~*]:al-pl-7 [&>svg+div]:al-translate-y-[-3px] [&>svg]:al-absolute [&>svg]:al-left-4 [&>svg]:al-top-4 [&>svg]:al-text-foreground",
    "[&>i~*]:al-pl-7 [&>i+div]:al-translate-y-[-3px] [&>i]:al-absolute [&>i]:al-left-4 [&>i]:al-top-4 [&>i]:al-text-foreground"
  ),
  {
    variants: {
      variant: {
        default: "al-bg-background al-text-foreground",
        destructive:
          "al-border-destructive/50 al-text-destructive dark:al-border-destructive [&>svg]:al-text-destructive [&>i]:al-text-destructive",
        warning:
          "al-border-yellow-500/50 al-text-yellow-800 al-bg-yellow-50 [&>svg]:al-text-yellow-600 [&>i]:al-text-yellow-600",
        success:
          "al-border-green-500/50 al-text-green-800 al-bg-green-50 [&>svg]:al-text-green-600 [&>i]:al-text-green-600",
        info: "al-border-blue-500/50 al-text-blue-800 al-bg-blue-50 [&>svg]:al-text-blue-600 [&>i]:al-text-blue-600",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const getAriaLabel = (variant: string | null | undefined): string => {
  switch (variant) {
    case "destructive":
      return "error-alert";
    case "warning":
      return "warning-alert";
    case "success":
      return "success-alert";
    case "info":
      return "info-alert";
    default:
      return "status-alert";
  }
};

const Alert = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof alertVariants>
>(({ className, variant, ...props }, ref) => (
  <div
    ref={ref}
    role="alert"
    aria-label={getAriaLabel(variant)}
    className={cn(alertVariants({ variant }), className)}
    {...props}
  />
));
Alert.displayName = "Alert";

const AlertTitle = React.forwardRef<HTMLParagraphElement, React.HTMLAttributes<HTMLHeadingElement>>(
  ({ className, children, ...props }, ref) => (
    <h5
      ref={ref}
      className={cn("al-mb-1 al-font-medium al-leading-none al-tracking-tight", className)}
      {...props}
    >
      {children}
    </h5>
  )
);
AlertTitle.displayName = "AlertTitle";

const AlertDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("al-text-sm [&_p]:al-leading-relaxed", className)}
    role="alert"
    aria-label="alert-description"
    {...props}
  />
));
AlertDescription.displayName = "AlertDescription";

export { Alert, AlertTitle, AlertDescription };
